// Task #4257 — DTO condivisi BE↔FE per ruoli, permessi, recinto (action scope)
// e concessioni permesso × portata. SoT unica: il backend li usa come tipi di
// ritorno/input dei service, il FE come generic di `apiClient.get<T>`.
// Modelliamo SOLO il payload `data` dell'envelope `{ success, data }`.

import { z } from 'zod';

/** timestamptz: ISO string sul wire, Date lato BE pre-serializzazione. */
const wireTimestamp = z.union([z.string(), z.date()]);

// ---------------------------------------------------------------------------
// Catalogo permessi e ruoli
// ---------------------------------------------------------------------------

export const permissionSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
    category: z.string(),
  })
  .passthrough();
export type Permission = z.infer<typeof permissionSchema>;

/**
 * Permesso collegato a un ruolo. PM-019: non esiste una «Portata» per permesso
 * del ruolo (la colonna `role_permissions.scope_level` è stata droppata); il
 * recinto si allarga SOLO con le concessioni per utente (allargamenti).
 */
export const rolePermissionSchema = permissionSchema;
export type RolePermission = z.infer<typeof rolePermissionSchema>;

/**
 * Tipo di accesso del ruolo (`roles.role_kind`), unica fonte del
 * `users.role` denormalizzato e quindi del bypass admin nei middleware:
 *  - admin → tutti i permessi, senza guardare la matrice del ruolo
 *  - agent → operatore: solo i permessi assegnati (ruolo "di centralino")
 *  - user  → solo i permessi assegnati (default per ogni ruolo custom)
 * Il NOME del ruolo non concorre mai alla risoluzione del tipo.
 */
export const roleKindSchema = z.enum(['admin', 'agent', 'user']);
export type RoleKind = z.infer<typeof roleKindSchema>;

export const roleSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string().nullable().optional(),
    name: z.string(),
    description: z.string().nullable(),
    is_system: z.boolean(),
    is_manager: z.boolean(),
    /** Tipo effettivo (già normalizzato dal backend: mai NULL sul wire). */
    role_kind: roleKindSchema,
    permissions: z.array(rolePermissionSchema),
    user_count: z.number(),
    created_at: wireTimestamp.optional(),
    updated_at: wireTimestamp.optional(),
  })
  .passthrough();
export type Role = z.infer<typeof roleSchema>;

export const rolesListResponseSchema = z.object({
  roles: z.array(roleSchema),
  total: z.number(),
});
export type RolesListResponse = z.infer<typeof rolesListResponseSchema>;

export const permissionsCatalogResponseSchema = z.object({
  permissions: z.array(permissionSchema),
  grouped: z.record(z.string(), z.array(permissionSchema)),
});
export type PermissionsCatalogResponse = z.infer<typeof permissionsCatalogResponseSchema>;

/**
 * Body POST/PUT /roles: solo i permessi. Un eventuale `scope_level` inviato da
 * client datati viene scartato (chiave sconosciuta: lo schema la ignora).
 */
export const rolePermissionInputSchema = z.object({
  permission_id: z.string().uuid(),
});
export type RolePermissionInput = z.infer<typeof rolePermissionInputSchema>;

export const roleUpsertSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  is_manager: z.boolean().optional(),
  /**
   * Tipo di accesso. In creazione default `user`; in modifica assente =
   * invariato. Sui ruoli di sistema non è modificabile; `admin` su un ruolo
   * custom richiede un chiamante admin del tenant (invariante nel service).
   */
  role_kind: roleKindSchema.optional(),
  permissions: z.array(rolePermissionInputSchema).default([]),
});
export type RoleUpsertInput = z.infer<typeof roleUpsertSchema>;

// ---------------------------------------------------------------------------
// Recinto (action scope) per utente
// ---------------------------------------------------------------------------

export const recintoModeSchema = z.enum(['all', 'restricted', 'none']);
export type RecintoMode = z.infer<typeof recintoModeSchema>;

export const scopeStoreSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
});
export type ScopeStore = z.infer<typeof scopeStoreSchema>;

export const scopeCompanySchema = z.object({
  id: z.string(),
  legal_name: z.string(),
  is_primary: z.boolean(),
  stores: z.array(scopeStoreSchema),
});
export type ScopeCompany = z.infer<typeof scopeCompanySchema>;

export const userScopeTreeSchema = z.object({
  companies: z.array(scopeCompanySchema),
});
export type UserScopeTree = z.infer<typeof userScopeTreeSchema>;

export const userScopeSchema = z.object({
  user_id: z.string(),
  mode: recintoModeSchema,
  company_ids: z.array(z.string()),
  store_ids: z.array(z.string()),
});
export type UserScope = z.infer<typeof userScopeSchema>;

export const userScopePutSchema = z.object({
  mode: recintoModeSchema,
  company_ids: z.array(z.string()),
  store_ids: z.array(z.string()),
});
export type UserScopePut = z.infer<typeof userScopePutSchema>;

// ---------------------------------------------------------------------------
// Concessioni: poteri aggiuntivi (kind 'potere') e allargamenti del recinto
// (kind 'allargamento'). Task #4303.
// ---------------------------------------------------------------------------

/**
 * Portata di una concessione:
 *  - base    → SOLO poteri: il permesso vale nel recinto effettivo della persona
 *  - stores  → sedi elencate (store_ids)        ┐
 *  - company → una ragione sociale (company_id) ├ SOLO allargamenti del recinto
 *  - tenant  → intero tenant                    ┘
 */
export const permissionGrantScopeLevelSchema = z.enum(['base', 'stores', 'company', 'tenant']);
export type PermissionGrantScopeLevel = z.infer<typeof permissionGrantScopeLevelSchema>;

/** Portata di un allargamento del recinto (mai 'base'). */
export const scopeExtensionLevelSchema = z.enum(['stores', 'company', 'tenant']);
export type ScopeExtensionLevel = z.infer<typeof scopeExtensionLevelSchema>;

/**
 * Tipo di concessione:
 *  - potere       → "può fare anche X" dentro il suo recinto effettivo
 *                   (permesso obbligatorio, portata = base)
 *  - allargamento → "opera anche su sede/RS/tenant" con TUTTI i suoi poteri
 *                   (nessun permesso, portata stores/company/tenant)
 */
export const permissionGrantKindSchema = z.enum(['potere', 'allargamento']);
export type PermissionGrantKind = z.infer<typeof permissionGrantKindSchema>;

export const permissionGrantSubjectKindSchema = z.enum(['user', 'role']);
export type PermissionGrantSubjectKind = z.infer<typeof permissionGrantSubjectKindSchema>;

/** Stato derivato in lettura (nessun cron): attiva, scaduta o revocata. */
export const permissionGrantStatusSchema = z.enum(['active', 'expired', 'revoked']);
export type PermissionGrantStatus = z.infer<typeof permissionGrantStatusSchema>;

/**
 * Motivo della chiusura (Task #4304). `manual` = revoca esplicita,
 * `replaced` = chiusa da una modifica (sostituzione tracciata: la riga nuova
 * è in `replaced_by_grant_id`). null su righe vive o revocate prima del
 * tracciamento del motivo (= revoca manuale legacy).
 */
export const permissionGrantRevokeReasonSchema = z.enum(['manual', 'replaced']);
export type PermissionGrantRevokeReason = z.infer<typeof permissionGrantRevokeReasonSchema>;

const permissionGrantCommonShape = {
  id: z.string(),
  tenant_id: z.string(),
  subject_kind: permissionGrantSubjectKindSchema,
  user_id: z.string().nullable(),
  role_id: z.string().nullable(),
  valid_until: wireTimestamp.nullable(),
  granted_by: z.string().nullable(),
  granted_by_name: z.string().nullable(),
  granted_at: wireTimestamp,
  revoked_at: wireTimestamp.nullable(),
  revoked_by: z.string().nullable(),
  revoked_by_name: z.string().nullable(),
  revoke_reason: permissionGrantRevokeReasonSchema.nullable(),
  /** Concessione che ha sostituito questa (solo con revoke_reason 'replaced'). */
  replaced_by_grant_id: z.string().nullable(),
  /** Concessione che questa ha sostituito (catena storica). */
  replaces_grant_id: z.string().nullable(),
  status: permissionGrantStatusSchema,
  /** Nome/email del beneficiario (solo lista tenant-wide). */
  user_name: z.string().nullable(),
  user_email: z.string().nullable(),
} as const;

/** Potere aggiuntivo: un permesso, valido nel recinto effettivo della persona. */
export const powerGrantSchema = z.object({
  ...permissionGrantCommonShape,
  kind: z.literal('potere'),
  permission_id: z.string(),
  permission_code: z.string(),
  permission_name: z.string(),
  permission_category: z.string(),
  scope_level: z.literal('base'),
  company_id: z.null(),
  company_name: z.null(),
  store_ids: z.array(z.string()),
  store_names: z.array(z.string()),
});
export type PowerGrant = z.infer<typeof powerGrantSchema>;

/** Allargamento del recinto: nessun permesso, vale per tutti i poteri. */
export const scopeExtensionGrantSchema = z.object({
  ...permissionGrantCommonShape,
  kind: z.literal('allargamento'),
  permission_id: z.null(),
  permission_code: z.null(),
  permission_name: z.null(),
  permission_category: z.null(),
  scope_level: scopeExtensionLevelSchema,
  company_id: z.string().nullable(),
  company_name: z.string().nullable(),
  store_ids: z.array(z.string()),
  store_names: z.array(z.string()),
});
export type ScopeExtensionGrant = z.infer<typeof scopeExtensionGrantSchema>;

export const permissionGrantSchema = z.discriminatedUnion('kind', [powerGrantSchema, scopeExtensionGrantSchema]);
export type PermissionGrant = z.infer<typeof permissionGrantSchema>;

/** ISO timestamp; null/assente = a tempo indeterminato. */
const grantValidUntilSchema = z.string().datetime({ offset: true }).nullable().optional();

/**
 * Crea un LOTTO di poteri aggiuntivi (una riga per permesso, stessa scadenza,
 * una transazione). Nessuna portata: valgono nel recinto effettivo.
 */
export const powerGrantsCreateSchema = z.object({
  permission_ids: z.array(z.string().uuid()).min(1).max(200),
  valid_until: grantValidUntilSchema,
});
export type PowerGrantsCreateInput = z.infer<typeof powerGrantsCreateSchema>;

const scopeExtensionShape = {
  scope_level: scopeExtensionLevelSchema,
  company_id: z.string().uuid().nullable().optional(),
  store_ids: z.array(z.string().uuid()).default([]),
  valid_until: grantValidUntilSchema,
} as const;

/** Coerenza portata ↔ dettaglio, condivisa da creazione e sostituzione. */
function refineScopeExtension(
  val: { scope_level: ScopeExtensionLevel; company_id?: string | null; store_ids: string[] },
  ctx: z.RefinementCtx,
): void {
    const stores = val.store_ids;
    if (val.scope_level === 'stores' && stores.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['store_ids'], message: 'portata "sedi elencate" richiede almeno una sede' });
    }
    if (val.scope_level !== 'stores' && stores.length > 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['store_ids'], message: 'store_ids ammessi solo con portata "sedi elencate"' });
    }
    if (val.scope_level === 'company' && !val.company_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['company_id'], message: 'portata "ragione sociale" richiede company_id' });
    }
    if (val.scope_level !== 'company' && val.company_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['company_id'], message: 'company_id ammesso solo con portata "ragione sociale"' });
    }
}

/** Crea un allargamento del recinto (stessa forma del recinto base + scadenza). */
export const scopeExtensionCreateSchema = z.object(scopeExtensionShape).superRefine(refineScopeExtension);
export type ScopeExtensionCreateInput = z.infer<typeof scopeExtensionCreateSchema>;

/**
 * Sostituzione tracciata (Task #4304): la modifica di una concessione crea una
 * riga nuova e chiude la precedente (motivo 'replaced') nella stessa
 * transazione, con le stesse validazioni della creazione. Il tipo dichiarato
 * deve coincidere con quello della concessione sostituita.
 *  - potere       → cambia SOLO la scadenza (il permesso non si modifica:
 *                   si revoca e si aggiunge dalla scheda utente)
 *  - allargamento → scadenza e/o portata
 */
export const powerGrantReplaceSchema = z.object({
  kind: z.literal('potere'),
  valid_until: grantValidUntilSchema,
});
export type PowerGrantReplaceInput = z.infer<typeof powerGrantReplaceSchema>;

export const scopeExtensionReplaceSchema = z
  .object({ kind: z.literal('allargamento'), ...scopeExtensionShape })
  .superRefine(refineScopeExtension);
export type ScopeExtensionReplaceInput = z.infer<typeof scopeExtensionReplaceSchema>;

export const permissionGrantReplaceSchema = z.union([powerGrantReplaceSchema, scopeExtensionReplaceSchema]);
export type PermissionGrantReplaceInput = z.infer<typeof permissionGrantReplaceSchema>;

/** Esito della sostituzione: la nuova concessione e quella chiusa. */
export const permissionGrantReplaceResponseSchema = z.object({
  grant: permissionGrantSchema,
  replaced: permissionGrantSchema,
});
export type PermissionGrantReplaceResponse = z.infer<typeof permissionGrantReplaceResponseSchema>;

export const permissionGrantsListResponseSchema = z.object({
  grants: z.array(permissionGrantSchema),
  total: z.number(),
});
export type PermissionGrantsListResponse = z.infer<typeof permissionGrantsListResponseSchema>;

/** Ordinamento della lista tenant-wide (default: scadenza più vicina prima, indeterminate in coda). */
export const permissionGrantsSortSchema = z.enum(['valid_until_asc', 'valid_until_desc', 'granted_at_desc']);
export type PermissionGrantsSort = z.infer<typeof permissionGrantsSortSchema>;

export const PERMISSION_GRANTS_PAGE_LIMIT_DEFAULT = 25;
export const PERMISSION_GRANTS_PAGE_LIMIT_MAX = 200;

export const permissionGrantsListQuerySchema = z.object({
  status: permissionGrantStatusSchema.optional(),
  kind: permissionGrantKindSchema.optional(),
  user_id: z.string().uuid().optional(),
  permission_code: z.string().optional(),
  /** Solo concessioni attive che scadono entro N giorni. */
  expiring_within_days: z.coerce.number().int().positive().max(365).optional(),
  /** Ricerca su persona (nome/email), permesso (nome/codice), ragione sociale e sede. */
  search: z.string().trim().max(200).optional(),
  sort: permissionGrantsSortSchema.optional(),
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(PERMISSION_GRANTS_PAGE_LIMIT_MAX).optional(),
});
export type PermissionGrantsListQuery = z.infer<typeof permissionGrantsListQuerySchema>;

/** Pagina della lista tenant-wide (paginazione server). */
export const permissionGrantsPageResponseSchema = permissionGrantsListResponseSchema.extend({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total_pages: z.number().int().nonnegative(),
});
export type PermissionGrantsPageResponse = z.infer<typeof permissionGrantsPageResponseSchema>;

// ---------------------------------------------------------------------------
// Permessi effettivi dell'utente corrente (GET /roles/my-permissions)
// ---------------------------------------------------------------------------

export const myPermissionsSchema = z.object({
  /** Codici permesso effettivi = ruolo ∪ concessioni utente valide. */
  permissions: z.array(z.string()),
});
export type MyPermissions = z.infer<typeof myPermissionsSchema>;

// ---------------------------------------------------------------------------
// Matrioska "chi vedo": policy per dominio + eccezioni per persona
// ---------------------------------------------------------------------------

/**
 * Domini serviti dal motore di autorità (`resolveAuthority`). Il codice
 * coincide con il prefisso dei permessi `<dominio>.view|manage`; i domini
 * con permessi storici diversi (`hr.requests`, `pos`, `telephony`,
 * `messaging`) sono mappati esplicitamente in `domainPermissionCodes`.
 *  - pos        → vendite e pratiche RMA/DOA per operatore (`created_by`);
 *                 chiusure di cassa, prima nota e corrispettivi restano
 *                 documenti contabili per registratore/sede (permesso
 *                 pos_amministrazione), come gli aggregati dashboard;
 *  - telephony  → CDR e widget "per interno" della persona (aggregati esclusi);
 *  - messaging  → conversazioni dei sender assegnati alle persone visibili.
 */
export const visibilityDomainSchema = z.enum([
  'crm',
  'incentivazione',
  'tasks',
  'calendar',
  'hr.shifts',
  'hr.attendance',
  'hr.requests',
  'pos',
  'telephony',
  'messaging',
]);
export type VisibilityDomain = z.infer<typeof visibilityDomainSchema>;
export const VISIBILITY_DOMAINS = visibilityDomainSchema.options;

/**
 * Policy CHI per dominio:
 *  - matrioska    → ognuno vede se stesso; i ruoli con tag manager vedono le
 *                   persone delle sedi del proprio recinto (± eccezioni);
 *  - tutti_vedono → chi ha il permesso di vista del dominio vede tutte le
 *                   persone del proprio recinto (solo lettura; la gestione
 *                   resta matrioska).
 * Riga assente = matrioska (fail-closed).
 */
export const visibilityPolicySchema = z.enum(['matrioska', 'tutti_vedono']);
export type VisibilityPolicy = z.infer<typeof visibilityPolicySchema>;

export const visibilityPoliciesSchema = z.record(visibilityDomainSchema, visibilityPolicySchema);
/** Mappa completa dominio → policy (il BE riempie sempre i default). */
export type VisibilityPolicies = Record<VisibilityDomain, VisibilityPolicy>;

export const visibilityPoliciesResponseSchema = z.object({
  policies: visibilityPoliciesSchema,
  updated_at: wireTimestamp.nullable(),
});
export type VisibilityPoliciesResponse = z.infer<typeof visibilityPoliciesResponseSchema>;

/** PUT parziale: solo i domini presenti vengono aggiornati. */
export const visibilityPoliciesPutSchema = z
  .object({ policies: visibilityPoliciesSchema })
  .refine((v) => Object.keys(v.policies).length > 0, { message: 'Nessuna policy da aggiornare' });
export type VisibilityPoliciesPut = z.infer<typeof visibilityPoliciesPutSchema>;

export const visibilityExceptionKindSchema = z.enum(['include', 'exclude']);
export type VisibilityExceptionKind = z.infer<typeof visibilityExceptionKindSchema>;

export const userVisibilityExceptionSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  /** Chi vede (titolare dell'eccezione). */
  manager_user_id: z.string(),
  /** Persona aggiunta (include) o tolta (exclude). */
  user_id: z.string(),
  kind: visibilityExceptionKindSchema,
  created_at: wireTimestamp,
  user_first_name: z.string().nullable(),
  user_last_name: z.string().nullable(),
  user_email: z.string().nullable(),
});
export type UserVisibilityException = z.infer<typeof userVisibilityExceptionSchema>;

export const userVisibilityExceptionUpsertSchema = z.object({
  user_id: z.string().uuid(),
  kind: visibilityExceptionKindSchema,
});
export type UserVisibilityExceptionUpsert = z.infer<typeof userVisibilityExceptionUpsertSchema>;
